import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Button, Flex } from 'antd';
import FadeIn from '../FadeIn';
import { BillingMode, OnboardingStep } from '../../services/exports/Constants';
import useApiClient from '../../hooks/api/useApiClient';
import MobileAppProduct from '../billing/MobileAppProduct';
import LoadingScreen from '../loading/LoadingScreen';
import { useCancelSubscriptionMutation, useCreateSubscriptionMutation } from '../../store/api-slices';

export default function MobileAppStep({ data, product, moveForward }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:MobileAppStep' });

  const { SubscriptionsManager, ServiceContractsManager, CurrentCompanyManager, MerchantProfileManager } =
    useApiClient();

  const [createSubscription] = useCreateSubscriptionMutation();
  const [cancelSubscription] = useCancelSubscriptionMutation();

  const [isSelected, setIsSelected] = useState(!!data);
  const [setupFee, setSetupFee] = useState(150);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingContract, setLoadingContract] = useState(true);

  useEffect(() => {
    loadContract();
  }, []);

  const loadContract = async () => {
    setLoadingContract(true);
    const { success, data: response } = await ServiceContractsManager.get();
    setLoadingContract(false);

    if (!success) {
      return toast.error(t('toasts.something_went_wrong'));
    }

    setContract(response);
    setSetupFee(!data && !response.netto_mobile_app_setup_fee ? 150 : response.netto_mobile_app_setup_fee);
  };

  const saveSubscription = async () => {
    if (data) {
      return true;
    }

    const { error } = await createSubscription({
      apiManager: SubscriptionsManager,
      data: {
        billing_mode: BillingMode.Stripe,
        trial_days: null,
        items: [{ product_id: product.id, quantity: 1 }],
      },
    });

    if (error) {
      toast.error(t('toasts.failed_to_save_changes'));

      return false;
    }

    return true;
  };

  const saveSetupFee = async () => {
    setLoading(true);
    const { success } = await ServiceContractsManager.save({
      netto_mobile_app_setup_fee: setupFee,
    });
    setLoading(false);

    if (!success) {
      toast.error(t('toasts.failed_to_save_changes'));

      return false;
    }

    return true;
  };

  const handleSubscriptionSelected = async () => {
    setLoading(true);
    const success = (await saveSubscription()) && (await saveSetupFee());
    if (!success) {
      return setLoading(false);
    }

    await CurrentCompanyManager.get();
    setLoading(false);
    moveForward && moveForward();
    toast.success(t('toasts.changes_got_saved'));
  };

  const handleSubscriptionNotSelected = async () => {
    setLoading(true);
    if (data) {
      const { error: subscriptionError } = await cancelSubscription({ id: data.id, apiManager: SubscriptionsManager });
      const { success: contractSuccess } = await ServiceContractsManager.save({
        netto_mobile_app_setup_fee: 0,
      });

      if (subscriptionError || !contractSuccess) {
        toast.error(t('toasts.something_went_wrong'));

        return setLoading(false);
      }
    }

    const { success } = await MerchantProfileManager.skipVerificationStep({ id: OnboardingStep.MobileApp });
    setLoading(false);

    success && moveForward && moveForward();
    !success && toast.error(t('toasts.something_went_wrong'));
  };

  const onConfirm = async () => {
    if (!isSelected) {
      return handleSubscriptionNotSelected();
    }

    return handleSubscriptionSelected();
  };

  return loadingContract ? (
    <LoadingScreen />
  ) : (
    <FadeIn>
      <h3 className="text-center">{t('title')}</h3>
      <p className="text-center text-gray-600 mt-xs">{t('description')}</p>
      <Flex justify="center" className="mt-lg">
        <MobileAppProduct
          data={product}
          isSelected={isSelected}
          setupFee={setupFee}
          discount={contract?.contract_discount_percentage}
          disabled={!!contract?.setup_fee_charged_at}
          onToggle={setIsSelected}
          onSetupFeeSelected={setSetupFee}
        />
      </Flex>
      <Flex justify="end" className="mt-xl">
        <Button type={isSelected ? 'primary' : 'default'} onClick={onConfirm} loading={loading} size="large">
          {isSelected ? t('buttons.continue') : t('buttons.no_app_for_now')}
        </Button>
      </Flex>
    </FadeIn>
  );
}
